import { inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@lentau/analytics/events/shared/services/analytics.service';
import {
  EventKind,
  EventProperties,
  HealthMetricStatus,
  MethodHandler,
  MethodProperties,
} from '@lentau/analytics/health-metrics/shared';
import { mapToSnowplowEvent } from '@lentau/analytics/health-metrics/shared';
import { METHOD_HANDLER_LIST, METHOD_PROPERTIES_LIST } from './handlers/handler-list';
import { HttpResponse, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { getMethodData } from './get-method-data';
import { checks } from './shared/checks';
import { CheckCondition, EventData } from './shared/interfaces';
import { getMethodNameFromOldRequest } from './shared/helper';
import { BASE_PATH } from 'angular-api-gateway/v1/variables';
import { HttpStatus } from '@lentau/ui-kit/core/enums/http-status.enum';
import { getCamelCaseNameFromUrl } from '@lentau/ui-kit/core/utils/url.utils';
import { HeaderProperties } from './shared/enums';

interface HMEventData {
  name: string;
  status?: string;
  properties?: EventProperties;
}

@Injectable({ providedIn: 'root' })
export class HealthMetricService {
  private analyticsService: AnalyticsService = inject(AnalyticsService);
  private agBasePath: string = inject(BASE_PATH);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public handleSuccess(response: HttpResponse<any>, request: HttpRequest<any>): void {
    if (!(response instanceof HttpResponse)) {
      return;
    }

    const traceId: string = this.getTraceId(response, request);

    const isAGetRequest: boolean = request.url.includes(this.agBasePath);
    if (isAGetRequest) {
      this.handleAGRequest(request, traceId);
      return;
    }

    const commonEventData: HMEventData = this.getCommonEventData(response, request);
    if (commonEventData?.properties) {
      this.handleOldRequest(commonEventData, traceId);

      return;
    }

    this.handleRestJrpcOkResult(response.body, traceId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public handleError(response: HttpErrorResponse, request: HttpRequest<any>): void {
    const isAGetRequest: boolean = request.url.includes(this.agBasePath);

    // Только для AG методов
    if (!(response instanceof HttpErrorResponse) || !isAGetRequest) {
      return;
    }

    if (response.status === HttpStatus.tooManyRequests) {
      return;
    }

    const traceId: string = this.getTraceId(response, request);
    const methodName: string = getCamelCaseNameFromUrl(request.url.replace(`${this.agBasePath}/`, ''));

    if (!methodName) {
      return;
    }

    const { status, props }: { status: HealthMetricStatus; props: EventProperties } = this.buildErrorEvent(response);
    const eventName: string = `${methodName}${status}`;
    this.sendEvent(methodName, eventName, props, traceId);
  }

  public manualHandleEvent(eventName: string, properties?: EventProperties): void {
    this.sendEvent('', eventName, properties, '');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getCommonEventData(response: HttpResponse<any>, req: HttpRequest<any>): HMEventData {
    const methodName: string = getMethodNameFromOldRequest(req.url, req.body);

    const eventData: EventData = methodName
      ? checks.find((check: CheckCondition) => check.condition(response))?.eventData
      : null;

    return {
      name: methodName,
      status: eventData?.status,
      properties: eventData?.properties,
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleRestJrpcOkResult(body: any, traceId: string): void {
    if (body) {
      const { methodName, methodData }: { methodName: string; methodData: unknown } = getMethodData(body);

      if (methodName) {
        this.handleCorrectMethod(methodName, methodData, traceId);
      }
    }
  }

  private handleCorrectMethod(methodName: string, body: unknown, traceId: string): void {
    const methodHandler: (body: unknown) => [] | EventProperties = this.handlerList[methodName];
    const eventName: string = methodName + HealthMetricStatus.Ok;
    this.sendEvent(methodName, eventName, methodHandler ? methodHandler(body) : undefined, traceId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private handleAGRequest(req: HttpRequest<any>, traceId: string): void {
    const methodName: string = getCamelCaseNameFromUrl(req.url.replace(`${this.agBasePath}/`, ''));
    const eventName: string = methodName + HealthMetricStatus.Ok;

    this.sendEvent(methodName, eventName, undefined, traceId);
  }

  private handleOldRequest(commonEventData: HMEventData, traceId: string): void {
    this.sendEvent(
      commonEventData.name,
      `${commonEventData.name}${commonEventData.status}`,
      commonEventData.properties,
      traceId
    );
  }

  private sendEvent(
    methodName: string,
    eventName: string,
    eventProperties: EventProperties = [],
    traceId: string
  ): void {
    const methodProperties: EventProperties = this.getMethodProperties(methodName);

    // Создаем новый массив вместо мутации существующего
    const allProperties: EventProperties = [
      ...eventProperties,
      {
        key: 'traceId',
        value: traceId,
      },
      ...methodProperties,
    ];

    this.analyticsService.pushToDataLayer(mapToSnowplowEvent(eventName, allProperties));
  }

  private getMethodProperties(methodName: string): EventProperties {
    const property: MethodProperties = METHOD_PROPERTIES_LIST.find((method: MethodProperties) =>
      method.get(methodName)
    );
    return property ? property.get(methodName) : [];
  }

  private get handlerList(): MethodHandler {
    return METHOD_HANDLER_LIST;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getTraceId(response: HttpResponse<any> | HttpErrorResponse, request: HttpRequest<any>): string {
    return (
      request.headers?.get(HeaderProperties.TRACEPARENT) ||
      response.headers?.get(HeaderProperties.TRACEPARENT) ||
      response.headers?.get(HeaderProperties.X_REQUEST_ID) ||
      ''
    );
  }

  private buildErrorEvent(response: HttpErrorResponse): { status: HealthMetricStatus; props: EventProperties } {
    const props: EventProperties = [];
    const isJson: boolean = this.isJsonError(response);
    const isServerError: boolean = response.status >= 500 && response.status < 600;
    const isWaf: boolean = response.status === 403 && !isJson;
    const isParsing: boolean = !isWaf && !isServerError && !isJson; // не json и не WAF

    if (isServerError) {
      props.push({ key: 'kind', value: 'server' }, { key: 'code', value: response.status.toString() });
      const status: HealthMetricStatus =
        response.status === 500 ? HealthMetricStatus.Warning : HealthMetricStatus.Error;
      return { status, props };
    }
    if (isWaf) {
      props.push({ key: 'kind', value: 'waf' });
      return { status: HealthMetricStatus.Error, props };
    }
    if (isParsing) {
      props.push({ key: 'kind', value: 'parsing' });
      return { status: HealthMetricStatus.Error, props };
    }

    // Остальные ошибки считаем Exception
    props.push({ key: 'kind', value: EventKind.NETWORK }, { key: 'code', value: response.status.toString() });
    return { status: HealthMetricStatus.Exception, props };
  }

  private isJsonError(response: HttpErrorResponse): boolean {
    const contentType: string = response.headers?.get('content-type') || '';
    if (contentType.toLowerCase().includes('application/json')) {
      return true;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const err: any = response.error;
    // Если ошибка уже объект, считаем как json
    return err && typeof err === 'object' && !(typeof err === 'string');
  }
}
